import { Badge } from 'designSystem/components/Badge'
import { Button } from 'designSystem/components/Button'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemMediaFrame,
  ItemTagRow,
  ItemTitle,
  ItemTitleText,
  ItemTrigger,
} from 'designSystem/components/Item'
import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { DocumentThumbnail } from 'features/knowledgeDocumentViewer/components/DocumentThumbnail'
import { formatDocumentDisplayTitle } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { ExternalLink, Star } from 'lucide-react'
import React from 'react'

const NON_BREAKING_SPACE_CHAR = '\u00a0'
const DRAWING_NUMBER_SEPARATOR = `${NON_BREAKING_SPACE_CHAR}-${NON_BREAKING_SPACE_CHAR}`

interface ExpandedDocumentCardProps {
  document: DocumentTableRow
  isSelected?: boolean
  isFavorited?: boolean
  onClick: (document: DocumentTableRow, event?: React.MouseEvent) => void
  onContextMenu?: (document: DocumentTableRow, event: React.MouseEvent) => void
  onToggleFavorite?: (document: DocumentTableRow) => void
}

function buildTagList(doc: DocumentTableRow): string[] {
  const tags: string[] = []
  if (doc.vendor) tags.push(doc.vendor)
  if (doc.docType) tags.push(doc.docType)
  if (doc.system) tags.push(doc.system)
  return tags
}

export const ExpandedDocumentCard: React.FC<ExpandedDocumentCardProps> = ({
  document,
  isSelected = false,
  isFavorited = false,
  onClick,
  onContextMenu,
  onToggleFavorite,
}) => {
  const handleClick = (event: React.MouseEvent) => {
    onClick(document, event)
  }

  const handleContextMenu = (event: React.MouseEvent) => {
    if (onContextMenu) {
      onContextMenu(document, event)
    }
  }

  const handleViewClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    onClick(document, event)
  }

  const handleToggleFavorite = (event: React.MouseEvent) => {
    event.stopPropagation()
    onToggleFavorite?.(document)
  }

  const rawDisplayTitle = document.documentTitle || document.title
  const displayTitle = formatDocumentDisplayTitle(
    document.documentTitle,
    document.title
  )
  const displayDrawingNumber =
    document.drawing ||
    (rawDisplayTitle !== document.title ? document.title : null)
  const tags = buildTagList(document)

  return (
    <Item
      size='md'
      variant={isSelected ? 'surfaceMuted' : 'surface'}
      onContextMenu={handleContextMenu}>
      <ItemTrigger
        type='button'
        aria-pressed={isSelected}
        onClick={handleClick}>
        <ItemMedia variant='thumbnail'>
          <ItemMediaFrame>
            <DocumentThumbnail
              documentId={document.id}
              alt={displayTitle}
              filename={document.filename}
              bqDocumentId={document.bqDocumentId}
              cantileverDocumentId={document.cantileverDocumentId}
              documentHostsHydrated={'cantileverDocumentId' in document}
            />
          </ItemMediaFrame>
        </ItemMedia>
        <ItemContent variant='document'>
          <ItemTitle variant='document'>
            <ItemTitleText>{displayTitle}</ItemTitleText>
            {displayDrawingNumber && (
              <ItemTitleText tone='muted'>
                {DRAWING_NUMBER_SEPARATOR}
                {displayDrawingNumber}
              </ItemTitleText>
            )}
          </ItemTitle>
          {document.summary && (
            <ItemDescription variant='document'>
              {document.summary}
            </ItemDescription>
          )}
          {tags.length > 0 && (
            <ItemTagRow>
              {tags.map((tag) => (
                <Badge key={tag} variant='outlineMuted'>
                  {tag}
                </Badge>
              ))}
            </ItemTagRow>
          )}
        </ItemContent>
      </ItemTrigger>
      <ItemActions variant='trailing'>
        <Button
          type='button'
          variant='inputRaised'
          size='default'
          onClick={handleViewClick}
          aria-label='View document'>
          View
          <ExternalLink size={16} />
        </Button>
        {onToggleFavorite && (
          <Button
            type='button'
            variant='inputRaised'
            size='icon'
            onClick={handleToggleFavorite}
            title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={
              isFavorited ? 'Remove from favorites' : 'Add to favorites'
            }
            aria-pressed={isFavorited}>
            <Star size={16} fill={isFavorited ? 'currentColor' : 'none'} />
          </Button>
        )}
      </ItemActions>
    </Item>
  )
}
